import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import './dotglobal-activity-digest.js';
import './dotglobal-langbar.js';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import { languageColor } from './dotglobal-language-colors.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TOrgTab = 'overview' | 'repos' | 'people';

/**
 * Organization view: banner (avatar, meta, member stack), Overview / Repositories / People
 * tabs, the AI digest, a recent/all repository list and a languages + people sidebar.
 * Mirrors the Claude Design Org view, wired to the real public org API. Extracted out of
 * dotglobal-app; emits `dotglobal-navigate` (and bubbles the digest's `dotglobal-toast`).
 */
@customElement('dotglobal-org-view')
export class DotglobalOrgView extends DeesElement {
  @property({ type: Object })
  public accessor data: interfaces.IReq_PublicOrg['response'] | null = null;

  @state()
  private accessor orgTab: TOrgTab = 'overview';

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; flex: 1; }
      .row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .empty { padding: 48px 20px; text-align: center; color: var(--text-muted); }
      .meta-item { display: inline-flex; align-items: center; gap: 6px; }
      .lang-dot { width: 11px; height: 11px; border-radius: 50%; background: var(--accent); }
      .nowrap { white-space: nowrap; }
      .sectitle { font-family: var(--font-display); font-weight: 700; letter-spacing: -0.01em; }

      .avatar {
        position: relative;
        width: 32px;
        height: 32px;
        border-radius: 9px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        overflow: hidden;
        color: var(--accent-fg);
        background: var(--accent);
        font-weight: 800;
        font-size: 13px;
      }
      .avatar.round { border-radius: 50%; }

      .org-banner { border-bottom: 1px solid var(--border-default); background: var(--bg-secondary); }
      .org-banner .page { padding-top: 28px; }
      .org-banner-head > .avatar { width: 84px; height: 84px; border-radius: 16px; font-size: 30px; }
      .org-headside { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
      .member-stack { display: flex; align-items: center; margin-right: 4px; }
      .member-stack .avatar { width: 30px; height: 30px; border: 2px solid var(--bg-secondary); }

      .tabs { display: flex; align-items: center; flex-wrap: wrap; margin-top: 14px; gap: 2px; }
      .tab {
        position: relative;
        top: 1px;
        height: 38px;
        padding: 0 12px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        border-bottom: 2px solid transparent;
        border-radius: 6px 6px 0 0;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: color 0.15s, background 0.15s;
      }
      .tab:hover { color: var(--text-primary); background: var(--hover); }
      .tab.on { color: var(--text-primary); border-bottom-color: var(--accent); }
      .tab .cnt { padding: 1px 6px; border-radius: 999px; color: var(--text-secondary); background: var(--bg-tertiary); font-size: 11px; font-weight: 600; }
      .tab.on .cnt { color: var(--accent); background: color-mix(in srgb, var(--accent) 16%, transparent); }

      .org-layout { display: grid; grid-template-columns: minmax(0, 1fr) 230px; gap: 24px; align-items: start; }
      .sidecol { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
      .org-langbar { height: 8px; display: flex; overflow: hidden; border-radius: 4px; background: var(--bg-tertiary); }
      .org-langbar span { display: block; }

      .navlink {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 34px;
        padding: 0 8px;
        border-radius: 6px;
        white-space: nowrap;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: color 0.15s, background 0.15s;
      }
      .navlink:hover { color: var(--text-primary); background: var(--hover); }

      .repocard {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 18px;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s;
      }
      .repocard:hover { border-color: var(--border-strong); background: var(--bg-secondary); }
      .org-cardgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(310px, 1fr)); gap: 14px; }
      .rcname { display: flex; align-items: center; gap: 8px; color: var(--accent); font-size: 15px; font-weight: 600; }
      .rcdesc { color: var(--text-secondary); font-size: 12.5px; line-height: 1.5; }
      .rcmeta { display: flex; align-items: center; gap: 16px; margin-top: 2px; color: var(--text-muted); font-size: 12px; flex-wrap: wrap; }

      .listcard { border: 1px solid var(--border-default); border-radius: 8px; background: var(--bg-primary); overflow: hidden; }
      .listrow { display: flex; align-items: flex-start; gap: 12px; padding: 12px 16px; border-bottom: 1px solid var(--border-subtle); }
      .listrow:last-child { border-bottom: 0; }
      .listrow:hover { background: var(--row-hover); }
      .org-reporow { align-items: center; cursor: pointer; }
      .listrow .it { flex-shrink: 0; margin-top: 1px; color: var(--text-muted); }
      .listrow .ti { color: var(--text-primary); font-size: 14.5px; font-weight: 600; }
      .listrow:hover .ti { color: var(--accent); }
      .listrow .sub { margin-top: 3px; color: var(--text-muted); font-size: 12px; }

      .people-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
      .people-card { flex-direction: row; align-items: center; gap: 12px; }
      .people-card .avatar { width: 42px; height: 42px; }

      @media (max-width: 900px) {
        .org-layout { grid-template-columns: minmax(0, 1fr); }
      }
    `,
  ];

  public render(): TemplateResult {
    const data = this.data;
    if (!data?.org) {
      return html`<div class="page"><div class="empty">Organization not found.</div></div>`;
    }
    const reposByUpdated = [...data.repos].sort((repoA, repoB) => new Date(repoB.updatedAt).getTime() - new Date(repoA.updatedAt).getTime());
    const pinnedRepos = reposByUpdated.slice(0, 6);
    const totalStars = data.repos.reduce((sum, repo) => sum + repo.starsCount, 0);
    const languages = this.getOrgLanguageEntries(data.repos);
    const hasMembers = data.members.length > 0;
    const activeOrgTab = this.orgTab === 'people' && !hasMembers ? 'overview' : this.orgTab;

    return html`
      <div class="stack" style="flex: 1">
        <div class="org-banner">
          <div class="page">
            <div class="row org-banner-head" style="align-items: flex-start; gap: 28px">
              ${this.renderAvatar(data.org.name, data.org.avatarUrl)}
              <div class="grow" style="min-width: 240px">
                <div class="row" style="gap: 10px; align-items: center">
                  <h1 style="margin: 0; font-size: 24px; font-weight: 800; letter-spacing: -0.02em">${data.org.fullName || data.org.name}</h1>
                  <span class="badge outline sq">${this.icon('Building')} Organization</span>
                </div>
                <p style="max-width: 620px; margin: 8px 0 0; color: var(--text-secondary); font-size: 14px; line-height: 1.55">
                  ${data.org.description || 'Public repositories mirrored into foss.global.'}
                </p>
                <div class="row" style="gap: 16px; margin-top: 12px; font-size: 12.5px">
                  <span class="meta-item">${this.icon('BookOpen')} <b style="color: var(--text-primary)">${data.stats.repoCount}</b> repositories</span>
                  ${hasMembers ? html`<span class="meta-item">${this.icon('User')} <b style="color: var(--text-primary)">${data.members.length}</b> people</span>` : ''}
                  <span class="meta-item">${this.icon('Star')} <b style="color: var(--text-primary)">${totalStars}</b> stars</span>
                  <span class="meta-item">${this.icon('CircleDot')} <b style="color: var(--text-primary)">${data.stats.openIssueCount}</b> open issues</span>
                  ${data.org.website ? html`<a class="meta-item" style="color: var(--accent)" href=${data.org.website} target="_blank" rel="noopener">${this.icon('Link')} ${this.displayHost(data.org.website)}</a>` : ''}
                  ${data.org.location ? html`<span class="meta-item">${this.icon('Globe')} ${data.org.location}</span>` : ''}
                </div>
              </div>
              <div class="org-headside">
                ${hasMembers
                  ? html`<div class="member-stack">${data.members.slice(0, 5).map((member, index) => html`<span style=${`margin-left:${index ? -8 : 0}px`}>${this.renderAvatar(member.login, member.avatarUrl, true)}</span>`)}</div>`
                  : ''}
                <span class="badge outline sq">${this.icon('Lock')} Read-only</span>
              </div>
            </div>
            <div class="tabs">
              <button class=${`tab ${activeOrgTab === 'overview' ? 'on' : ''}`} @click=${() => { this.orgTab = 'overview'; }}>${this.icon('BookOpen')} Overview</button>
              <button class=${`tab ${activeOrgTab === 'repos' ? 'on' : ''}`} @click=${() => { this.orgTab = 'repos'; }}>${this.icon('Package')} Repositories <span class="cnt">${data.repos.length}</span></button>
              ${hasMembers ? html`<button class=${`tab ${activeOrgTab === 'people' ? 'on' : ''}`} @click=${() => { this.orgTab = 'people'; }}>${this.icon('User')} People <span class="cnt">${data.members.length}</span></button>` : ''}
            </div>
          </div>
        </div>

        <div class="page content">
          <div class="org-layout">
            <div class="stack" style="gap: 20px; min-width: 0">
              ${activeOrgTab === 'overview'
                ? html`<dotglobal-activity-digest .activity=${data.activity} .heading=${`What ${data.org.name} shipped recently`}></dotglobal-activity-digest>`
                : ''}
              ${activeOrgTab === 'people'
                ? this.renderPeopleList(data.members)
                : activeOrgTab === 'repos'
                  ? this.renderRepoList(data.org, reposByUpdated)
                  : this.renderRecentRepos(data.org, pinnedRepos)}
            </div>
            ${this.renderSidebar(languages, data.members)}
          </div>
        </div>
      </div>
    `;
  }

  private renderRecentRepos(orgArg: interfaces.IPublicOrg, reposArg: interfaces.IPublicRepo[]): TemplateResult {
    return html`
      <div class="stack" style="gap: 14px">
        <div class="row" style="justify-content: space-between; gap: 10px">
          <div class="sectitle" style="font-size: 16px">Recent repositories</div>
          <button class="navlink" @click=${() => { this.orgTab = 'repos'; }}>View all ${orgArg.repoCount || reposArg.length} -></button>
        </div>
        ${reposArg.length
          ? html`
              <div class="org-cardgrid">
                ${reposArg.map((repo) => html`
                  <div class="repocard" @click=${() => this.navigate(`#/repo/${repo.org}/${repo.name}`)}>
                    <div class="row" style="justify-content: space-between; gap: 10px">
                      <div class="rcname">${this.icon('BookOpen')} <b>${repo.name}</b></div>
                      ${repo.language ? html`<span class="badge def sq" style="font-size: 10.5px"><span class="lang-dot" style=${`background:${languageColor(repo.language)}`}></span>${repo.language}</span>` : ''}
                    </div>
                    <div class="rcdesc">${repo.description || 'No description provided.'}</div>
                    <div class="rcmeta">
                      ${repo.language ? html`<span class="meta-item"><span class="lang-dot" style=${`background:${languageColor(repo.language)}`}></span>${repo.language}</span>` : ''}
                      <span class="meta-item">${this.icon('Star')} ${repo.starsCount}</span>
                      <span class="meta-item">${this.icon('CircleDot')} ${repo.openIssuesCount}</span>
                      <span class="meta-item">${this.icon('Clock')} ${this.timeAgo(repo.updatedAt)}</span>
                    </div>
                  </div>
                `)}
              </div>
            `
          : html`<div class="empty">No public repositories found.</div>`}
      </div>
    `;
  }

  private renderRepoList(orgArg: interfaces.IPublicOrg, reposArg: interfaces.IPublicRepo[]): TemplateResult {
    return html`
      <div class="stack" style="gap: 14px">
        <div class="row" style="justify-content: space-between; gap: 10px">
          <div class="sectitle" style="font-size: 16px">All repositories</div>
          <span class="muted meta-item" style="font-size: 12px">${this.icon('Clock')} Most recently updated</span>
        </div>
        <div class="listcard">
        ${reposArg.length
          ? reposArg.map((repo) => html`
              <div class="listrow org-reporow" @click=${() => this.navigate(`#/repo/${repo.org}/${repo.name}`)}>
                <span class="it" style="color: var(--accent); margin-top: 0">${this.icon('BookOpen')}</span>
                <div style="min-width: 0; flex: 1">
                  <div class="row" style="gap: 8px; align-items: center">
                    <span class="ti">${repo.name}</span>
                    ${repo.archived ? html`<span class="badge outline sq" style="font-size: 10px">archived</span>` : ''}
                  </div>
                  <div class="sub" style="font-size: 12.5px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 540px">${repo.description || 'No description provided.'}</div>
                  <div class="row" style="gap: 14px; margin-top: 6px; font-size: 12px">
                    ${repo.language ? html`<span class="meta-item muted"><span class="lang-dot" style=${`background:${languageColor(repo.language)}`}></span>${repo.language}</span>` : ''}
                    <span class="meta-item muted">${this.icon('Star')} ${repo.starsCount}</span>
                    <span class="meta-item muted">${this.icon('CircleDot')} ${repo.openIssuesCount}</span>
                    ${repo.topics.slice(0, 2).map((topic) => html`<span class="badge def sq" style="font-size: 10.5px">${topic}</span>`)}
                  </div>
                </div>
                <div class="muted nowrap" style="font-size: 12.5px; align-self: flex-start; margin-top: 2px">Updated ${this.timeAgo(repo.updatedAt)}</div>
              </div>
            `)
          : html`<div class="empty">No public repositories found for ${orgArg.name}.</div>`}
        </div>
      </div>
    `;
  }

  private renderPeopleList(membersArg: interfaces.IPublicUser[]): TemplateResult {
    return html`
      <div class="stack" style="gap: 14px">
        <div class="sectitle" style="font-size: 16px">People</div>
        <div class="people-grid">
          ${membersArg.map((member) => html`
            <div class="repocard people-card">
              ${this.renderAvatar(member.login, member.avatarUrl, true)}
              <div style="min-width: 0">
                <div style="font-weight: 600; font-size: 14px">${member.fullName || member.login}</div>
                <div class="muted mono" style="font-size: 12px">${member.login}</div>
              </div>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  private renderSidebar(languagesArg: Array<[string, number]>, membersArg: interfaces.IPublicUser[]): TemplateResult {
    const total = languagesArg.reduce((sum, [, value]) => sum + value, 0);
    return html`
      <aside class="sidecol">
        <div class="stack" style="gap: 10px">
          <div style="font-weight: 600; font-size: 13px">Languages</div>
          ${languagesArg.length
            ? html`
                <dotglobal-langbar .items=${languagesArg} .barHeight=${8} .radius=${4}></dotglobal-langbar>
                <div class="row" style="gap: 12px; font-size: 12px; flex-wrap: wrap">
                  ${languagesArg.map(([language, value]) => html`
                    <span class="meta-item"><span class="lang-dot" style=${`background:${languageColor(language)}`}></span>${language} ${total ? Math.round((value / total) * 100) : 0}%</span>
                  `)}
                </div>
              `
            : html`<div class="muted" style="font-size: 12.5px">No language data yet.</div>`}
        </div>
        <div class="divider"></div>
        ${membersArg.length
          ? html`
              <div class="stack" style="gap: 10px">
                <div style="font-weight: 600; font-size: 13px">People</div>
                <div class="row" style="gap: 6px">
                  ${membersArg.slice(0, 12).map((member) => html`<span title=${member.fullName || member.login}>${this.renderAvatar(member.login, member.avatarUrl, true)}</span>`)}
                </div>
              </div>
              <div class="divider"></div>
            `
          : ''}
        <div class="stack" style="gap: 8px; font-size: 12.5px">
          <span class="meta-item">${this.icon('Lock')} Public read-only mirror</span>
        </div>
      </aside>
    `;
  }

  private getOrgLanguageEntries(reposArg: interfaces.IPublicRepo[]): Array<[string, number]> {
    const languageCounts = new Map<string, number>();
    for (const repo of reposArg) {
      if (repo.language) {
        languageCounts.set(repo.language, (languageCounts.get(repo.language) ?? 0) + 1);
      }
    }
    return Array.from(languageCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }


  private displayHost(urlArg: string): string {
    try {
      return new URL(urlArg).host;
    } catch {
      return urlArg;
    }
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${15}></dees-icon>`;
  }

  private renderAvatar(labelArg: string, avatarUrlArg: string, roundArg = false): TemplateResult {
    const initials = (labelArg || 'fg').split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((partArg) => partArg[0]?.toUpperCase()).join('') || 'fg';
    return html`
      <span class=${`avatar ${roundArg ? 'round' : ''}`}>
        ${initials}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    return `${Math.round(days / 30)}mo ago`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-org-view': DotglobalOrgView;
  }
}
